// src/admin/pages/Restaurants/AddMenuItemModal.js
import React, { useState, useEffect } from "react";
import "./AddMenuItemModal.css";

export default function AddMenuItemModal({
  menu,
  onMenuUpdated,
  categories,
  onClose,
}) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [active, setActive] = useState(true);

  // Gestione categoria
  const [selectedCategory, setSelectedCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [useNewCategory, setUseNewCategory] = useState(false);

  const [error, setError] = useState("");

  // Ingredienti caricati da /restaurant_ingredients
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [recipeItems, setRecipeItems] = useState([]);

  // Per aggiungere un nuovo ingrediente
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [newQty, setNewQty] = useState(1);

  useEffect(() => {
    fetchIngredients();
    // eslint-disable-next-line
  }, []);

  const fetchIngredients = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_API_URL}/restaurant_ingredients?restaurant_id=${menu.restaurant_id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error("Errore nel caricamento degli ingredienti del ristorante");
      }
      const data = await res.json();
      const mapped = data.map((ri) => ({
        id: ri.ingredient_id,
        name: ri.ingredient_name,
        unit: ri.unit,
      }));
      setAvailableIngredients(mapped);
    } catch (err) {
      console.error("fetchIngredients error:", err);
    }
  };

  const handleAddRecipeItem = () => {
    if (!selectedIngredientId) return;
    const exists = recipeItems.find(
      (ri) => ri.ingredientId === parseInt(selectedIngredientId, 10)
    );
    if (exists) {
      alert("Ingrediente già presente nella ricetta.");
      return;
    }
    const newItem = {
      ingredientId: parseInt(selectedIngredientId, 10),
      qtyRequired: parseFloat(newQty),
    };
    setRecipeItems((prev) => [...prev, newItem]);
    setSelectedIngredientId("");
    setNewQty(1);
  };

  const handleRemoveRecipeItem = (ingredientId) => {
    setRecipeItems((prev) => prev.filter((ri) => ri.ingredientId !== ingredientId));
  };

  const handleQtyChange = (ingredientId, value) => {
    setRecipeItems((prev) =>
      prev.map((ri) =>
        ri.ingredientId === ingredientId
          ? { ...ri, qtyRequired: parseFloat(value) }
          : ri
      )
    );
  };

  const handleCreateMenuItem = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      const finalCategory = useNewCategory ? newCategory : selectedCategory;
      const categoryToSend = finalCategory ? finalCategory.trim() : null;

      // 1) Crea piatto
      const resItem = await fetch(`${process.env.REACT_APP_API_URL}/menu`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          price: parseFloat(price),
          category: categoryToSend,
          active,
        }),
      });
      if (!resItem.ok) {
        const errData = await resItem.json();
        throw new Error(errData.detail || "Errore creazione piatto");
      }
      const newItem = await resItem.json();

      // 2) Aggiunge l'ID del nuovo piatto al menù
      const currentIds = menu.items.map((mi) => mi.id);
      const updatedIds = [...currentIds, newItem.id];
      const patchRes = await fetch(`${process.env.REACT_APP_API_URL}/menus/${menu.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ menu_item_ids: updatedIds }),
      });
      if (!patchRes.ok) {
        const errData = await patchRes.json();
        throw new Error(errData.detail || "Errore associazione piatto al menù");
      }

      // 3) Crea recipe items
      for (const ri of recipeItems) {
        const recipeRes = await fetch(`${process.env.REACT_APP_API_URL}/recipe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            menu_item_id: newItem.id,
            ingredient_id: ri.ingredientId,
            qty_required: ri.qtyRequired,
          }),
        });
        if (!recipeRes.ok) {
          const errData = await recipeRes.json();
          console.error("Errore creazione recipe item:", errData);
        }
      }

      // 4) Rifetch menù per avere dati aggiornati
      const freshRes = await fetch(`${process.env.REACT_APP_API_URL}/menus/${menu.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!freshRes.ok) {
        throw new Error("Errore caricamento menù aggiornato");
      }
      const freshMenu = await freshRes.json();

      // 5) Notifica il parent
      onMenuUpdated(freshMenu);

      // 6) Chiude la modale
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  // Evita che il click sull'area interna chiuda la modale
  const handleInnerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      {/* Su mobile occuperà quasi tutto lo schermo */}
      <div className="modal-container" onClick={handleInnerClick}>
        <div className="modal-header">
          <h3 className="modal-title">Aggiungi Piatto a: {menu.name}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <p className="error-text">{error}</p>}

          {/* Sezione: dati base */}
          <div className="section-block">
            <h4>Dati Base</h4>
            <div className="form-row">
              <label>Nome Piatto</label>
              <input
                type="text"
                placeholder="Es: Spaghetti al Pomodoro"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <label>Prezzo</label>
              <input
                type="number"
                step="0.01"
                placeholder="Es: 7.50"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <label>Categoria</label>
              {!useNewCategory && (
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">(nessuna categoria)</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
              {useNewCategory && (
                <input
                  type="text"
                  placeholder="Nuova categoria"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                />
              )}
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={useNewCategory}
                  onChange={(e) => setUseNewCategory(e.target.checked)}
                />
                <span>Nuova?</span>
              </label>
            </div>
            <div className="form-row checkbox-row">
              <label>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Attivo</span>
              </label>
            </div>
          </div>

          {/* Sezione: ingredienti / ricetta */}
          <div className="section-block">
            <h4>Ingredienti</h4>
            <div className="ingredient-row">
              <select
                value={selectedIngredientId}
                onChange={(e) => setSelectedIngredientId(e.target.value)}
              >
                <option value="">-- Seleziona ingrediente --</option>
                {availableIngredients.map((ing) => (
                  <option key={ing.id} value={ing.id}>
                    {ing.name} {ing.unit ? `(${ing.unit})` : ""}
                  </option>
                ))}
              </select>
              <input
                type="number"
                step="0.01"
                placeholder="Quantità"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
              />
              <button type="button" className="add-ingredient-btn" onClick={handleAddRecipeItem}>
                Aggiungi
              </button>
            </div>

            {recipeItems.length > 0 && (
              <table className="recipe-table">
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th>Quantità</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recipeItems.map((ri) => {
                    const ing = availableIngredients.find((i) => i.id === ri.ingredientId);
                    if (!ing) return null;
                    return (
                      <tr key={ri.ingredientId}>
                        <td>
                          {ing.name}
                          {ing.unit ? ` (${ing.unit})` : ""}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={ri.qtyRequired}
                            onChange={(e) => handleQtyChange(ri.ingredientId, e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-ingredient-btn"
                            onClick={() => handleRemoveRecipeItem(ri.ingredientId)}
                          >
                            Rimuovi
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pulsanti finali */}
          <div className="modal-actions">
            <button className="save-btn" onClick={handleCreateMenuItem}>
              Salva
            </button>
            <button className="cancel-btn" onClick={onClose}>
              Annulla
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
